import { PermissionVerbsV1, PermissionVerbsV2 } from '@innovation-os-ng/core/core';
import { intersection } from '@innovation-os-ng/helpers';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AUTH_CONTEXT_FEATURE_SELECTOR } from '../constants';
import { AuthContextSpace } from '../interfaces/auth-context-space.ineterface';
import { AuthContextTenant } from '../interfaces/auth-context-tenant.interface';
import { AuthContextState } from '../interfaces/auth-context.interface';

const selectAuthContextFeature = createFeatureSelector<AuthContextState>(AUTH_CONTEXT_FEATURE_SELECTOR);
const selectAuthContext = createSelector(selectAuthContextFeature, (state: AuthContextState) => state);
const selectCurrentSpaceActions = createSelector(selectAuthContextFeature, (state: AuthContextState) => state.currentSpace?.actions);
const selectCurrentTenantActions = createSelector(selectAuthContextFeature, (state: AuthContextState) => state.currentTenant?.actions);
const selectCurrentTenantFeatures = createSelector(selectAuthContextFeature, (state: AuthContextState) => state.currentTenant?.features);
const selectAuthContextSpaces = createSelector(selectAuthContext, (state: AuthContextState) => state.spaces);
const selectAuthContextTenants = createSelector(selectAuthContext, (state: AuthContextState) => state.tenants);
const selectAuthContextLoading = createSelector(selectAuthContext, (state: AuthContextState) => state.isLoading);
const selectAuthContextLoaded = createSelector(selectAuthContextFeature, (state: AuthContextState) => state.result.loaded);
const selectAllModuleSubscriptions = createSelector(selectAuthContextTenants, (tenants: { [tenantUri: string]: AuthContextTenant }) => {
  const moduleSubs = {};
  Object.entries(tenants).forEach((pair) => {
    moduleSubs[`${pair[0]}`] = pair[1].subscriptions;
  });
  return moduleSubs;
});
const selectAllFeatures = createSelector(selectAuthContextTenants, (tenants: { [tenantUri: string]: AuthContextTenant }) => {
  const features = {};
  Object.entries(tenants).forEach((pair) => {
    features[`${pair[0]}`] = pair[1].features;
  });
  return features;
});

const selectTenantModules = (tenantUri: string) =>
  createSelector(selectAllModuleSubscriptions, (modules: { [tenant_uri: string]: Array<string> }) => modules[tenantUri]);

const selectTenantFeatures = (tenantUri: string) =>
  createSelector(selectAllFeatures, (features: { [tenant_uri: string]: Array<string> }) => features[tenantUri]);

const selectTenantsActions = (tenantUri: string) =>
  createSelector(selectAuthContextTenants, (tenants: { [tenant_uri: string]: AuthContextTenant }) => {
    return tenants[tenantUri]?.actions;
  });

const selectSpaceActions = (spaceUri: string) =>
  createSelector(selectAuthContextSpaces, (spaces: { [space_uri: string]: AuthContextSpace }) => {
    return spaces[spaceUri]?.actions;
  });

const selectSpacesByPermission = (permission: PermissionVerbsV1 | PermissionVerbsV2 | string) =>
  createSelector(selectAuthContextSpaces, (spaces: { [space_uri: string]: AuthContextSpace }) => {
    return Object.entries(spaces)
      .filter((pair) => pair[1].actions.includes(permission))
      .map(([spaceUri, space]) => {
        return { ...space, spaceUri };
      });
  });

const selectSpaceUrisByAnyPermissions = (permissions: Array<PermissionVerbsV1 | PermissionVerbsV2 | string>) =>
  createSelector(selectAuthContextSpaces, (spaces: { [space_uri: string]: AuthContextSpace }) => {
    return Object.entries(spaces)
      .filter((pair) => intersection(permissions, pair[1].actions)?.length)
      .map((space) => space[0]);
  });

const selectSpaceUrisByTenantUri = (tenantUri: string) =>
  createSelector(selectAuthContextSpaces, (spaces: { [space_uri: string]: AuthContextSpace }) => {
    return Object.entries(spaces)
      .filter((pair) => pair[1].tenantUri === tenantUri)
      .map((pair) => {
        return pair[0];
      });
  });

const selectSpaceUrisByTenantSlug = (tenantSlug: string) =>
  createSelector(
    selectAuthContextSpaces,
    selectTenantUriBySlug(tenantSlug),
    (spaces: { [space_uri: string]: AuthContextSpace }, tenantUri: string) => {
      return Object.entries(spaces)
        .filter((pair) => pair[1].tenantUri === tenantUri)
        .map((pair) => {
          return pair[0];
        });
    }
  );

const selectTenantUriBySlug = (tenanSlug: string) =>
  createSelector(selectAuthContextTenants, (tenants: { [tenantUri: string]: AuthContextTenant }) => {
    const tenant: [string, AuthContextTenant] = Object.entries(tenants).find((pair) => pair[1].tenantSlug === tenanSlug);
    if (tenant) {
      return tenant[0];
    } else {
      return null;
    }
  });

const selectCurrentPermissions = createSelector(
  selectCurrentSpaceActions,
  selectCurrentTenantActions,
  (spaceActions: Array<string>, tenantActions: Array<string>) => {
    return { spaceActions, tenantActions };
  }
);
const selectHasAnyPermissions = (action: PermissionVerbsV1 | PermissionVerbsV2) =>
  createSelector(selectCurrentPermissions, (permissions: { spaceActions: Array<string>; tenantActions: Array<string> }) => {
    return permissions.spaceActions.includes(action) || permissions.tenantActions.includes(action);
  });

const selectElementTypeActions = (spaceUri: string, elementTypeUri: string) =>
  createSelector(selectAuthContextSpaces, (spaces: { [space_uri: string]: AuthContextSpace }) => {
    return spaces[spaceUri]?.elementTypes?.[elementTypeUri];
  });

const selectCurrentSpaceElementTypeActions = (elementTypeUri: string) =>
  createSelector(selectAuthContext, (state: AuthContextState) => {
    return state.currentSpace?.elementTypes?.[elementTypeUri];
  });

const selectCurrentSpaceCombinedElementTypeActions = createSelector(selectAuthContext, (state: AuthContextState) => {
  let typeActions = [];
  if (state.currentSpace?.elementTypes) {
    Object.values(state.currentSpace?.elementTypes).forEach((action) => {
      typeActions.push(...action);
    });
  }

  // Lets remove redundant data
  typeActions = [...new Set(typeActions)];
  return typeActions;
});

export const AuthContextSelectors = {
  selectAuthContext,
  selectAuthContextSpaces,
  selectAuthContextTenants,
  selectAuthContextLoading,
  selectAuthContextLoaded,
  selectAllFeatures,
  selectAllModuleSubscriptions,
  selectTenantModules,
  selectTenantFeatures,
  selectTenantsActions,
  selectSpaceActions,
  selectSpacesByPermission,
  selectSpaceUrisByTenantUri,
  selectSpaceUrisByTenantSlug,
  selectTenantUriBySlug,
  selectCurrentPermissions,
  selectHasAnyPermissions,
  selectSpaceUrisByAnyPermissions,
  selectElementTypeActions,
  selectCurrentSpaceElementTypeActions,
  selectCurrentTenantFeatures,
  selectCurrentSpaceCombinedElementTypeActions,
  selectCurrentSpaceActions,
  selectCurrentTenantActions
};
